import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Trash2, ArrowLeft, Search, AlertTriangle, Database, X, Edit } from 'lucide-react';

const DatabaseManagement = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [profits, setProfits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [interests, setInterests] = useState([]);
  const [userBankAccounts, setUserBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    if (user?.role !== 'superadmin') {
      navigate('/bess/dashboard');
      return;
    }
    fetchAllData();
  }, [user]);

  const fetchAllData = async () => {
    try {
      const [usersRes, investmentsRes, profitsRes, projectsRes, interestsRes, bankAccountsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/investments`),
        axios.get(`${API}/profits`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/interest`),
        axios.get(`${API}/user-bank-accounts/all/pending`).catch(() => ({ data: [] }))
      ]);

      setUsers(usersRes.data);
      setInvestments(investmentsRes.data);
      setProfits(profitsRes.data);
      setProjects(projectsRes.data);
      setInterests(interestsRes.data);
      
      // Get all user bank accounts
      const allBankAccounts = [];
      for (const u of usersRes.data.filter(user => user.role === 'user')) {
        try {
          const bankRes = await axios.get(`${API}/user-bank-accounts/${u.id}`);
          allBankAccounts.push(...bankRes.data);
        } catch (err) {
          // ignore
        }
      }
      setUserBankAccounts(allBankAccounts);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`⚠️ WARNUNG: Möchten Sie den User "${username}" wirklich unwiderruflich löschen?\n\nDies kann nicht rückgängig gemacht werden!`)) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('User erfolgreich gelöscht');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleDeleteInvestment = async (investmentId, investmentNumber) => {
    if (!window.confirm(`⚠️ WARNUNG: Möchten Sie die Investition "${investmentNumber}" wirklich unwiderruflich löschen?\n\nDies kann nicht rückgängig gemacht werden!`)) {
      return;
    }

    try {
      await axios.delete(`${API}/investments/${investmentId}`);
      toast.success('Investition erfolgreich gelöscht');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleDeleteProfit = async (profitId) => {
    if (!window.confirm(`⚠️ WARNUNG: Möchten Sie diesen Ertrag-Eintrag wirklich unwiderruflich löschen?\n\nDies kann nicht rückgängig gemacht werden!`)) {
      return;
    }

    try {
      await axios.delete(`${API}/profits/${profitId}`);
      toast.success('Ertrag erfolgreich gelöscht');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleDeleteProject = async (projectId, projectTitle) => {
    if (!window.confirm(`⚠️ WARNUNG: Möchten Sie das Projekt "${projectTitle}" wirklich unwiderruflich löschen?\n\nAlle zugehörigen Investitionen und Erträge sollten zuerst gelöscht werden!\n\nDies kann nicht rückgängig gemacht werden!`)) {
      return;
    }

    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success('Projekt erfolgreich gelöscht');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleDeleteInterest = async (interestId, projectId) => {
    if (!window.confirm(`⚠️ WARNUNG: Möchten Sie dieses Interesse wirklich unwiderruflich löschen?\n\nDies kann nicht rückgängig gemacht werden!`)) {
      return;
    }

    try {
      await axios.delete(`${API}/interest/${interestId}`);
      toast.success('Interesse erfolgreich gelöscht');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const startEditItem = (item, type) => {
    setEditingItem({ ...item, type });
    setEditForm(item);
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setEditForm({});
  };

  const handleSaveEdit = async () => {
    try {
      if (editingItem.type === 'project') {
        // Send only editable fields
        const projectUpdate = {
          title: editForm.title,
          location: editForm.location,
          total_shares: editForm.total_shares,
          available_shares: editForm.available_shares,
          price_per_share: editForm.price_per_share
        };
        await axios.put(`${API}/projects/${editingItem.id}`, projectUpdate);
        toast.success('Projekt aktualisiert');
      } else if (editingItem.type === 'user') {
        const userUpdate = {
          username: editForm.username,
          email: editForm.email,
          role: editForm.role,
          phone: editForm.phone,
          is_verified: editForm.is_verified
        };
        await axios.put(`${API}/users/${editingItem.id}`, userUpdate);
        toast.success('User aktualisiert');
      }
      cancelEdit();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
      console.error('Edit error:', error.response);
    }
  };

  const filterUsers = () => {
    if (!searchTerm) return users;
    return users.filter(u => 
      u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filterInvestments = () => {
    if (!searchTerm) return investments;
    return investments.filter(inv => 
      inv.investment_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.user_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.project_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filterProfits = () => {
    if (!searchTerm) return profits;
    return profits.filter(p => 
      p.project_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.month?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filterProjects = () => {
    if (!searchTerm) return projects;
    return projects.filter(p => 
      p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.location?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filterInterests = () => {
    if (!searchTerm) return interests;
    return interests.filter(i => 
      i.user_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.project_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.status?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filterBankAccounts = () => {
    if (!searchTerm) return userBankAccounts;
    return userBankAccounts.filter(b => 
      b.account_holder?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.iban?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getProjectTitle = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.title || projectId;
  };

  const getUserName = (userId) => {
    const foundUser = users.find(u => u.id === userId);
    return foundUser?.username || userId;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 p-8">
        <p className="text-center">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/bess/dashboard')}
            className="flex items-center text-slate-600 hover:text-slate-800 mb-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Zurück zum Dashboard
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Database size={32} className="text-red-600" />
            <h1 className="text-4xl font-bold text-slate-900">Datenbankverwaltung</h1>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 mt-4">
            <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-sm text-red-900 font-semibold">Nur für SuperAdmin</p>
              <p className="text-xs text-red-700">Gelöschte Einträge können nicht wiederhergestellt werden. Bitte mit Vorsicht verwenden!</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Suchen..."
              className="pl-10"
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="users">Users ({users.length})</TabsTrigger>
            <TabsTrigger value="projects">Projects ({projects.length})</TabsTrigger>
            <TabsTrigger value="investments">Investments ({investments.length})</TabsTrigger>
            <TabsTrigger value="interests">Interessen ({interests.length})</TabsTrigger>
            <TabsTrigger value="profits">Erträge ({profits.length})</TabsTrigger>
            <TabsTrigger value="bankaccounts">Bankkonten ({userBankAccounts.length})</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800 text-white">
                    <tr>
                      <th className="text-left p-4 font-semibold">ID</th>
                      <th className="text-left p-4 font-semibold">Username</th>
                      <th className="text-left p-4 font-semibold">Email</th>
                      <th className="text-left p-4 font-semibold">Rolle</th>
                      <th className="text-left p-4 font-semibold">Verifiziert</th>
                      <th className="text-left p-4 font-semibold">Erstellt</th>
                      <th className="text-right p-4 font-semibold">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterUsers().map((u, index) => (
                      <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-4 text-xs text-slate-500 font-mono">{u.id.substring(0, 8)}...</td>
                        <td className="p-4 font-medium">{u.username}</td>
                        <td className="p-4 text-slate-600">{u.email}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            u.role === 'superadmin' ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' :
                            u.role === 'admin' ? 'bg-red-100 text-red-800' :
                            u.role === 'finance' ? 'bg-blue-100 text-blue-800' :
                            'bg-slate-100 text-slate-800'
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="p-4">
                          {u.is_verified ? (
                            <span className="text-green-600 font-semibold">✓</span>
                          ) : (
                            <span className="text-red-600">✗</span>
                          )}
                        </td>
                        <td className="p-4 text-sm text-slate-600">
                          {new Date(u.created_at).toLocaleDateString('de-DE')}
                        </td>
                        <td className="p-4 text-right">
                          {u.role === 'superadmin' && u.id === user.id ? (
                            <span className="text-xs text-slate-500 italic">Selbst</span>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50 border-red-200"
                              onClick={() => handleDeleteUser(u.id, u.username)}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filterUsers().length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  Keine Users gefunden
                </div>
              )}
            </div>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800 text-white">
                    <tr>
                      <th className="text-left p-4 font-semibold">ID</th>
                      <th className="text-left p-4 font-semibold">Titel</th>
                      <th className="text-left p-4 font-semibold">Standort</th>
                      <th className="text-left p-4 font-semibold">Total Shares</th>
                      <th className="text-left p-4 font-semibold">Verfügbare Shares</th>
                      <th className="text-left p-4 font-semibold">Preis/Share</th>
                      <th className="text-left p-4 font-semibold">Status</th>
                      <th className="text-right p-4 font-semibold">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterProjects().map((project, index) => (
                      <tr key={project.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-4 text-xs text-slate-500 font-mono">{project.id.substring(0, 8)}...</td>
                        <td className="p-4 font-medium">{project.title}</td>
                        <td className="p-4 text-slate-600">{project.location}</td>
                        <td className="p-4">{project.total_shares}</td>
                        <td className="p-4 font-semibold text-green-600">{project.available_shares}</td>
                        <td className="p-4">€{project.price_per_share?.toLocaleString()}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            project.project_status === 'completed' ? 'bg-green-100 text-green-800' :
                            project.project_status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {project.project_status || 'planned'}
                          </span>
                        </td>
                        <td className="p-4 text-right flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 hover:bg-blue-50"
                            onClick={() => startEditItem(project, 'project')}
                          >
                            Bearbeiten
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50 border-red-200"
                            onClick={() => handleDeleteProject(project.id, project.title)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filterProjects().length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  Keine Projekte gefunden
                </div>
              )}
            </div>
          </TabsContent>

          {/* Investments Tab */}
          <TabsContent value="investments">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800 text-white">
                    <tr>
                      <th className="text-left p-4 font-semibold">ID</th>
                      <th className="text-left p-4 font-semibold">Nummer</th>
                      <th className="text-left p-4 font-semibold">User</th>
                      <th className="text-left p-4 font-semibold">Projekt</th>
                      <th className="text-left p-4 font-semibold">Anteile</th>
                      <th className="text-left p-4 font-semibold">Betrag</th>
                      <th className="text-left p-4 font-semibold">Status</th>
                      <th className="text-left p-4 font-semibold">Erstellt</th>
                      <th className="text-right p-4 font-semibold">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterInvestments().map((inv, index) => (
                      <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-4 text-xs text-slate-500 font-mono">{inv.id.substring(0, 8)}...</td>
                        <td className="p-4 font-medium">{inv.investment_number || 'N/A'}</td>
                        <td className="p-4 text-slate-600 text-sm">{getUserName(inv.user_id)}</td>
                        <td className="p-4 text-slate-600 text-sm">{getProjectTitle(inv.project_id)}</td>
                        <td className="p-4">{inv.shares}</td>
                        <td className="p-4 font-semibold">€{inv.total_amount?.toLocaleString()}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            inv.payment_status === 'bezahlt' ? 'bg-green-100 text-green-800' :
                            inv.payment_status === 'teilweise_bezahlt' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {inv.payment_status || 'offen'}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-slate-600">
                          {new Date(inv.created_at).toLocaleDateString('de-DE')}
                        </td>
                        <td className="p-4 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50 border-red-200"
                            onClick={() => handleDeleteInvestment(inv.id, inv.investment_number)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filterInvestments().length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  Keine Investments gefunden
                </div>
              )}
            </div>
          </TabsContent>

          {/* Interests Tab */}
          <TabsContent value="interests">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800 text-white">
                    <tr>
                      <th className="text-left p-4 font-semibold">ID</th>
                      <th className="text-left p-4 font-semibold">User</th>
                      <th className="text-left p-4 font-semibold">Projekt</th>
                      <th className="text-left p-4 font-semibold">Status</th>
                      <th className="text-left p-4 font-semibold">Erstellt</th>
                      <th className="text-right p-4 font-semibold">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterInterests().map((interest, index) => (
                      <tr key={interest.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-4 text-xs text-slate-500 font-mono">{interest.id.substring(0, 8)}...</td>
                        <td className="p-4 text-slate-600">{getUserName(interest.user_id)}</td>
                        <td className="p-4 text-slate-600">{getProjectTitle(interest.project_id)}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            interest.status === 'approved' ? 'bg-green-100 text-green-800' :
                            interest.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {interest.status}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-slate-600">
                          {new Date(interest.created_at).toLocaleDateString('de-DE')}
                        </td>
                        <td className="p-4 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50 border-red-200"
                            onClick={() => handleDeleteInterest(interest.id, interest.project_id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filterInterests().length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  Keine Interessen gefunden
                </div>
              )}
            </div>
          </TabsContent>

          {/* Profits Tab */}
          <TabsContent value="profits">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800 text-white">
                    <tr>
                      <th className="text-left p-4 font-semibold">ID</th>
                      <th className="text-left p-4 font-semibold">Projekt</th>
                      <th className="text-left p-4 font-semibold">Monat</th>
                      <th className="text-left p-4 font-semibold">Betrag</th>
                      <th className="text-left p-4 font-semibold">Erstellt</th>
                      <th className="text-right p-4 font-semibold">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterProfits().map((profit, index) => (
                      <tr key={profit.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-4 text-xs text-slate-500 font-mono">{profit.id.substring(0, 8)}...</td>
                        <td className="p-4 text-slate-600">{getProjectTitle(profit.project_id)}</td>
                        <td className="p-4 font-medium">{profit.month}</td>
                        <td className="p-4 font-bold text-green-600">€{profit.amount?.toLocaleString()}</td>
                        <td className="p-4 text-sm text-slate-600">
                          {profit.created_at ? new Date(profit.created_at).toLocaleDateString('de-DE') : 'N/A'}
                        </td>
                        <td className="p-4 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50 border-red-200"
                            onClick={() => handleDeleteProfit(profit.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filterProfits().length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  Keine Ertrage gefunden
                </div>
              )}
            </div>
          </TabsContent>

          {/* Bank Accounts Tab */}
          <TabsContent value="bankaccounts">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800 text-white">
                    <tr>
                      <th className="text-left p-4 font-semibold">ID</th>
                      <th className="text-left p-4 font-semibold">User</th>
                      <th className="text-left p-4 font-semibold">Projekt</th>
                      <th className="text-left p-4 font-semibold">Kontoinhaber</th>
                      <th className="text-left p-4 font-semibold">IBAN</th>
                      <th className="text-left p-4 font-semibold">Status</th>
                      <th className="text-left p-4 font-semibold">Erstellt</th>
                      <th className="text-right p-4 font-semibold">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterBankAccounts().map((account, index) => (
                      <tr key={account.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-4 text-xs text-slate-500 font-mono">{account.id.substring(0, 8)}...</td>
                        <td className="p-4 text-slate-600">{getUserName(account.user_id)}</td>
                        <td className="p-4 text-slate-600">{getProjectTitle(account.project_id)}</td>
                        <td className="p-4 font-medium">{account.account_holder}</td>
                        <td className="p-4 font-mono text-sm">{account.iban}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            account.status === 'approved' ? 'bg-green-100 text-green-800' :
                            account.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {account.status}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-slate-600">
                          {new Date(account.created_at).toLocaleDateString('de-DE')}
                        </td>
                        <td className="p-4 text-right">
                          <span className="text-xs text-slate-500 italic">Nur über User bearbeiten</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filterBankAccounts().length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  Keine Bankkonten gefunden
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Edit Modal */}
        {editingItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-slate-900">
                  {editingItem.type === 'project' ? 'Projekt bearbeiten' : 'User bearbeiten'}
                </h2>
                <button onClick={cancelEdit} className="text-slate-500 hover:text-slate-700">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                {editingItem.type === 'project' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Titel</label>
                      <Input
                        value={editForm.title || ''}
                        onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Standort</label>
                      <Input
                        value={editForm.location || ''}
                        onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Total Shares</label>
                        <Input
                          type="number"
                          value={editForm.total_shares || ''}
                          onChange={(e) => setEditForm({...editForm, total_shares: parseInt(e.target.value)})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Verfügbare Shares</label>
                        <Input
                          type="number"
                          value={editForm.available_shares || ''}
                          onChange={(e) => setEditForm({...editForm, available_shares: parseInt(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Preis pro Share</label>
                      <Input
                        type="number"
                        value={editForm.price_per_share || ''}
                        onChange={(e) => setEditForm({...editForm, price_per_share: parseFloat(e.target.value)})}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Username</label>
                      <Input
                        value={editForm.username || ''}
                        onChange={(e) => setEditForm({...editForm, username: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Email</label>
                      <Input
                        value={editForm.email || ''}
                        onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Rolle</label>
                      <select
                        value={editForm.role || ''}
                        onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                        className="w-full border rounded p-2"
                      >
                        <option value="user">user</option>
                        <option value="admin">admin</option>
                        <option value="finance">finance</option>
                        <option value="content_filler">content_filler</option>
                        <option value="superadmin">superadmin</option>
                      </select>
                    </div>
                  </>
                )}

                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" onClick={cancelEdit}>
                    Abbrechen
                  </Button>
                  <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700">
                    Speichern
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatabaseManagement;
